import { useEffect, useMemo } from 'react';
import { BiPieChartAlt2 } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';

import { getDateRangeFromPeriod } from '@/components';
import type { PieChartItem } from '@/components/PieChart/types';
import { BaseWidget } from '@/components/Widget/BaseWidget';
import { useTransactionsStore } from '@/store/expenses-store/transactions/transactions.store.ts';
import { aggregateSpendingByCategory } from '@pages/Expenses/helpers/aggregateSpending';
import CategorySpending from '@pages/Manage/components/CategorySpending.tsx';

export const SpendingByCategoryWidget = () => {
  const navigate = useNavigate();

  const {
    transactions,
    getAllTransactions,
    transactionCategories,
    getTransactionsCategories,
    loading,
    error,
  } = useTransactionsStore();

  const year = new Date().getFullYear().toString();

  const dateRange = useMemo(
    () => getDateRangeFromPeriod({ month: 'January', year, period: 'yearly' }),
    [year],
  );

  useEffect(() => {
    void getTransactionsCategories();
  }, [getTransactionsCategories]);

  useEffect(() => {
    void getAllTransactions(dateRange);
  }, [getAllTransactions, dateRange]);

  const categories = useMemo(
    () =>
      aggregateSpendingByCategory(transactions, transactionCategories).map((row) => ({
        title: row.title,
        sum: row.spent,
        icon: row.icon,
        color: row.color,
        slug: row.slug,
      })),
    [transactions, transactionCategories],
  );

  const items: PieChartItem[] = useMemo(
    () =>
      categories.map((category, index) => ({
        id: index + 1,
        title: category.title,
        sum: category.sum,
        icon: category.icon,
        color: category.color,
      })),
    [categories],
  );

  return (
    <BaseWidget headerTitle="Budget" headerIcon={<BiPieChartAlt2 />}>
      <CategorySpending
        plain
        items={items}
        categories={categories}
        periodLabel={year}
        loading={loading}
        error={error}
        onRetry={() => {
          void getTransactionsCategories();
          void getAllTransactions(dateRange);
        }}
        onSelectCategory={(slug) => navigate(`/manage/budget-details/${encodeURIComponent(slug)}`)}
      />
    </BaseWidget>
  );
};

export default SpendingByCategoryWidget;
